import React, { useState, useEffect } from "react";
import { useUser } from "../../context/UserContext";
import { useNavigate } from "react-router-dom";
import AccessPanel from "../AccessPanel";
import NotificationPanel from "../NotificationPanel";
import { supabase } from "../../supabaseClient";

function TopBar({ darkMode, setDarkMode }) {
    const { currentUser, logout } = useUser();

    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [isAccessPanelOpen, setIsAccessPanelOpen] = useState(false);
    const [notificationOpen, setNotificationOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);



    const navigate = useNavigate();

    const fetchNotifications = React.useCallback(async () => {
        if (!currentUser?.id) return;

        const { data, error } = await supabase
            .from("notifications")
            .select(`
                *,
                task:tasks(*)
            `)
            .eq("user_id", currentUser.id)
            .eq("is_dismissed", false)
            .in("type", ["TASK_CREATED", "TASK_EXTENSION_REQUEST"])
            .order("created_at", { ascending: false });

        if (!error) {
            setNotifications(data || []);
        } else {
            console.error("Fetch notifications error:", error);
        }
    }, [currentUser?.id]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleUserManagementClick = () => {
        setIsProfileOpen(false);
        navigate("/users");
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/login");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    return (
        <>
            <header
                className="h-24 flex-shrink-0 flex items-center justify-center px-8 transition-colors duration-300 relative"
                style={{ backgroundColor: "#86bb46" }}
            >
                <div className="absolute left-8 top-1/2 -translate-y-1/2 flex items-center topbar-left-section">
                    <button
                        onClick={() => window.dispatchEvent(new CustomEvent("toggle-sidebar"))}
                        className="mobile-hamburger-btn mr-3 p-1 rounded hover:bg-white/10 text-white"
                        title="Toggle Menu"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <img
                        src="/image (8).png"
                        alt="AGS Logo"
                        className="h-16 w-auto object-contain topbar-left-logo"
                    />
                </div>

                <div className="flex flex-col justify-center items-center text-center topbar-center-section">
                    <h1 className="text-3xl font-bold text-white tracking-wide leading-tight">
                        AGS TASK PLANNER
                    </h1>
                    <p className="text-sm text-highlight mt-1 font-bold tracking-wide">
                        {currentUser?.role || "Guest Mode"}
                    </p>
                </div>

                <div className="absolute right-8 top-1/2 -translate-y-1/2 flex items-center justify-end gap-4 topbar-right-section">
                    <button
                        onClick={() => setNotificationOpen(true)}
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-all relative"
                        title="Notifications"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                        </svg>

                        {notifications.length > 0 && (
                            <span
                                style={{
                                    position: "absolute",
                                    top: "-5px",
                                    right: "-5px",
                                    background: "#ef4444",
                                    color: "white",
                                    borderRadius: "999px",
                                    minWidth: "16px",
                                    height: "16px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "10px",
                                    fontWeight: "800",
                                    padding: "0 4px"
                                }}
                            >
                                {notifications.length}
                            </span>
                        )}
                    </button>

                    <button
                        onClick={() => setIsInfoModalOpen(true)}
                        className="w-8 h-8 rounded-full flex items-center justify-center bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-all"
                        title="Application Information"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>

                    <button
                        onClick={() => setIsProfileOpen(true)}
                        className="h-10 flex items-center cursor-pointer hover:scale-105 transition-all focus:outline-none p-0 bg-transparent"
                        title="Open Profile"
                    >
                        <img src="/AGS-logo.png" alt="Banner Logo" className="h-10 w-auto object-contain topbar-right-logo" />
                    </button>
                </div>
            </header>

            <NotificationPanel
                open={notificationOpen}
                onClose={() => setNotificationOpen(false)}
                notifications={notifications}
                currentUser={currentUser}
                darkMode={darkMode}
                onDismiss={async (id) => {
                    await supabase
                        .from("notifications")
                        .update({ is_dismissed: true })
                        .eq("id", id);

                    fetchNotifications();
                }}
                onOpenTask={async (notification) => {
                    const taskId = notification.task_id || notification.task?.id;

                    if (!taskId) return;

                    setNotificationOpen(false);
                    navigate(`/tasks/${taskId}`);
                }}
            />


            {isInfoModalOpen && (
                <div className="info-modal-overlay">
                    <div className="absolute inset-0" onClick={() => setIsInfoModalOpen(false)} />
                    <div className="info-modal-container">
                        <div className="info-modal-header">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-[#86bb46]/10 flex items-center justify-center text-[#86bb46] border border-[#86bb46]/20">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="info-modal-title">About AGS Task Planner</h3>
                            </div>
                            <button onClick={() => setIsInfoModalOpen(false)} className="info-modal-close">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="info-modal-body">
                            <p className="info-modal-description">
                                AGS Task Planner is a comprehensive operational platform designed to track and manage tasks, teams, and workflows.
                            </p>

                            <div>
                                <div className="info-card info-card-green">
                                    <h4>TASK MANAGEMENT</h4>
                                    <p>Efficiently log and track tasks, deadlines, and priorities across all departments.</p>
                                </div>

                                <div className="info-card info-card-blue">
                                    <h4>TEAM COLLABORATION</h4>
                                    <p>Manage teams and sub-teams for seamless workflows and resource allocation.</p>
                                </div>

                                <div className="info-card info-card-purple">
                                    <h4>PERFORMANCE INSIGHTS</h4>
                                    <p>Visualize productivity metrics, activity logs, and status updates to ensure operational excellence.</p>
                                </div>
                            </div>
                        </div>

                        <div className="info-modal-footer">
                            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                                Version 1.0.1
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <>
                {isProfileOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-30 transition-all duration-500"
                        onClick={() => setIsProfileOpen(false)}
                    />
                )}

                <div className={`slide-in-panel ${isProfileOpen ? "open" : ""} p-0 flex flex-col items-center shadow-2xl transition-all duration-300 ${darkMode ? "bg-gray-900/95" : "bg-white/95"} backdrop-blur-md`}>
                    <div className="w-full flex justify-start px-6 pt-6">
                        <button
                            onClick={() => setIsProfileOpen(false)}
                            style={{
                                border: "none",
                                background: "transparent",
                                padding: "8px",
                                cursor: "pointer",
                                color: darkMode ? "#cbd5e1" : "#475569",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "transform 0.2s ease"
                            }}
                            className="hover:scale-110 active:scale-95"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {currentUser && (
                        <div className="flex-1 w-full px-8 flex flex-col items-center pt-2">
                            <div
                                style={{
                                    width: 140,
                                    height: 140,
                                    borderRadius: "50%",
                                    border: "3px solid #84BD3A",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 20px",
                                    background: "transparent",
                                    boxShadow: "0 4px 14px rgba(132, 189, 58, 0.12)"
                                }}
                            >
                                <img
                                    src="/image (8).png"
                                    alt="AGS"
                                    style={{
                                        width: "80%",
                                        height: "80%",
                                        objectFit: "contain",
                                    }}
                                />
                            </div>

                            <h2 
                                className="font-bold mb-1 text-center tracking-tight"
                                style={{
                                    fontSize: "24px",
                                    color: darkMode ? "#f8fafc" : "#1e293b",
                                }}
                            >
                                {currentUser.name}
                            </h2>

                            <p 
                                className="mb-5 text-center"
                                style={{
                                    fontSize: "15px",
                                    fontWeight: "400",
                                    color: darkMode ? "#94a3b8" : "#64748b",
                                }}
                            >
                                {currentUser.email}
                            </p>

                            {/* Role Pill */}
                            <div
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#84BD3A",
                                    color: "#ffffff",
                                    padding: "10px 26px",
                                    borderRadius: 999,
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    letterSpacing: "0.2px",
                                    marginBottom: "28px"
                                }}
                            >
                                {currentUser.role}
                            </div>

                            {/* Thin Separator */}
                            <div
                                style={{
                                    width: "100%",
                                    height: "1.5px",
                                    backgroundColor: darkMode ? "#334155" : "#f1f5f9",
                                    marginBottom: "24px",
                                }}
                            />

                            {/* Menu Buttons (Vertically Aligned left-column icons) */}
                            <div className="w-full flex flex-col gap-4">
                                {(currentUser.role !== "User" || currentUser.can_manage_users) && (
                                    <button
                                        onClick={handleUserManagementClick}
                                        className={`w-full h-[54px] rounded-[16px] border flex items-center justify-start pl-[78px] gap-3 transition-all active:scale-[0.98] ${
                                            darkMode 
                                                ? "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-750" 
                                                : "border-slate-200/80 bg-white text-slate-800 hover:bg-slate-50 hover:shadow-sm"
                                        }`}
                                        style={{ fontSize: "15px", fontWeight: "600" }}
                                    >
                                        <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" className={darkMode ? "text-slate-400" : "text-slate-500"}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        User management
                                    </button>
                                )}

                                {(currentUser.role === "Global Admin" ||
                                    currentUser.role === "Operational Admin") && (
                                        <button
                                            onClick={() => {
                                                setIsProfileOpen(false);
                                                setIsAccessPanelOpen(true);
                                            }}
                                            className={`w-full h-[54px] rounded-[16px] border flex items-center justify-start pl-[78px] gap-3 transition-all active:scale-[0.98] ${
                                                darkMode 
                                                    ? "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-750" 
                                                    : "border-slate-200/80 bg-white text-slate-800 hover:bg-slate-50 hover:shadow-sm"
                                            }`}
                                            style={{ fontSize: "15px", fontWeight: "600" }}
                                        >
                                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" className={darkMode ? "text-slate-400" : "text-slate-500"}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            Access Panel
                                        </button>
                                    )}

                                <button
                                    onClick={() => {
                                        setIsProfileOpen(false);
                                        navigate("/change-log");
                                    }}
                                    className={`w-full h-[54px] rounded-[16px] border flex items-center justify-start pl-[78px] gap-3 transition-all active:scale-[0.98] ${
                                        darkMode 
                                            ? "border-slate-700 bg-slate-800 text-slate-100 hover:bg-slate-750" 
                                            : "border-slate-200/80 bg-white text-slate-800 hover:bg-slate-50 hover:shadow-sm"
                                    }`}
                                    style={{ fontSize: "15px", fontWeight: "600" }}
                                >
                                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" className={darkMode ? "text-slate-400" : "text-slate-500"}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Change Log
                                </button>
                            </div>

                            {/* Thin Separator */}
                            <div
                                style={{
                                    width: "100%",
                                    height: "1.5px",
                                    backgroundColor: darkMode ? "#334155" : "#f1f5f9",
                                    marginTop: "24px",
                                    marginBottom: "24px",
                                }}
                            />

                            {/* Theme Toggle Section */}
                            <div className="w-full flex flex-col items-center px-4 mt-4 mb-4">
                                <h3 
                                    style={{ 
                                        fontSize: "18px", 
                                        fontWeight: "700", 
                                        color: darkMode ? "#f8fafc" : "#1e293b",
                                        marginBottom: "20px"
                                    }}
                                >
                                    Theme
                                </h3>

                                <div className="w-full flex items-center justify-center gap-6 mb-3">
                                    <button 
                                        onClick={() => setDarkMode(true)}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                                        title="Dark Mode"
                                        className="transition-transform active:scale-90 hover:scale-110"
                                    >
                                        <svg width="28" height="28" fill={darkMode ? "#84BD3A" : "none"} stroke={darkMode ? "#84BD3A" : "#64748b"} strokeWidth="2.2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                        </svg>
                                    </button>

                                    <div 
                                        className="flex items-center justify-center cursor-pointer"
                                        onClick={() => setDarkMode(!darkMode)}
                                    >
                                        <div 
                                            style={{
                                                width: "80px",
                                                height: "36px",
                                                backgroundColor: darkMode ? "#334155" : "#e2e8f0",
                                                borderRadius: "18px",
                                                position: "relative",
                                                transition: "background-color 0.3s ease",
                                                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)"
                                            }}
                                        >
                                            <div 
                                                style={{
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: "#84BD3A",
                                                    borderRadius: "50%",
                                                    position: "absolute",
                                                    top: "4px",
                                                    left: darkMode ? "48px" : "4px",
                                                    transition: "left 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)",
                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)"
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <button 
                                        onClick={() => setDarkMode(false)}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0 }}
                                        title="Light Mode"
                                        className="transition-transform active:scale-90 hover:scale-110"
                                    >
                                        <svg width="28" height="28" fill={!darkMode ? "#84BD3A" : "none"} stroke={!darkMode ? "#84BD3A" : "#64748b"} strokeWidth="2.2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m0-12.728l.707.707m12.728 12.728l.707.707M12 7a5 5 0 100 10 5 5 0 000-10z" />
                                        </svg>
                                    </button>
                                </div>

                                <span 
                                    style={{ 
                                        fontSize: "15px", 
                                        fontWeight: "600", 
                                        color: darkMode ? "#94a3b8" : "#64748b",
                                        marginTop: "4px"
                                    }}
                                >
                                    {darkMode ? 'Dark mode' : 'Light mode'}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="w-full px-8 pb-8 pt-4 mt-auto">
                        <button
                            onClick={handleLogout}
                            className={`w-full h-[54px] rounded-[16px] font-bold text-base flex items-center justify-center gap-3 transition-all shadow-sm active:scale-95 hover:brightness-110`}
                            style={{
                                backgroundColor: "#ef4444",
                                color: "#ffffff",
                                border: "none"
                            }}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </>

            {isAccessPanelOpen && (
                <div
                    className="modal-backdrop"
                    onClick={() => setIsAccessPanelOpen(false)}
                    style={{
                        background: "rgba(17, 24, 39, 0.7)",
                        backdropFilter: "blur(8px)",
                        zIndex: 1000
                    }}
                >
                    <div
                        className="modal-card shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "800px",
                            width: "90%",
                            padding: 0,
                            overflow: "hidden",
                            borderRadius: "24px",
                            background: darkMode ? "#1f2937" : "white",
                            display: "flex",
                            flexDirection: "column",
                            maxHeight: "85vh",
                            border: darkMode ? "1px solid #374151" : "none"
                        }}
                    >
                        <div className="p-6 border-b flex items-center justify-between" style={{ background: "#76AB3F", color: "white" }}>
                            <div>
                                <h3 className="font-bold text-lg leading-none">
                                    Administrative Access Control
                                </h3>
                                <p className="text-white/80 text-xs mt-1">
                                    Configure section-specific management privileges
                                </p>
                            </div>

                            <button
                                onClick={() => setIsAccessPanelOpen(false)}
                                className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ flex: 1, overflow: "hidden", minHeight: 0, display: "flex", flexDirection: "column" }}>
                            <AccessPanel darkMode={darkMode} onClose={() => setIsAccessPanelOpen(false)} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default TopBar;